// src/components/stock/PackProductsTab.js
/**
 * Pack Products Management Tab Component
 * 
 * Displays and manages products that are sold as packs/bundles.
 * Features:
 * - View pack products with confidence levels
 * - Filter by confidence, brand, verification status
 * - Manual pack size override
 * - Bulk verification
 * - Refresh inference from sales data
 */

import React, { useEffect, useState } from 'react';
import { 
    Table, Button, Tag, Space, Input, Select, message, Modal, 
    Tooltip, Checkbox, InputNumber, Spin, Empty
} from 'antd';
import { 
    ReloadOutlined, SearchOutlined, CheckCircleOutlined, 
    EditOutlined, ExclamationCircleOutlined,
    CheckOutlined, WarningOutlined,
    ClearOutlined, QuestionCircleOutlined, PlusOutlined
} from '@ant-design/icons';
import packProductsService from '../../services/packProductsService';
import { useAuth } from '../../context/AuthContext';
import './PackProductsTab.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;
const { confirm } = Modal;

const PackProductsTab = () => {
    const { user } = useAuth();
    
    // Data state
    const [products, setProducts] = useState([]);
    const [stats, setStats] = useState(null);
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    
    // Pagination
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });
    
    // Filters
    const [filters, setFilters] = useState({
        search: '',
        confidenceLevel: null,
        brand: null,
        verifiedOnly: false,
        needsReview: false,
        sortBy: 'sample_size',
        sortOrder: 'DESC',
    });
    
    // Selection state for bulk actions
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    
    // Edit modal state
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editForm, setEditForm] = useState({
        manual_pack_size: null,
        is_verified: false,
        notes: '',
    });
    const [saving, setSaving] = useState(false);

    // Add modal state (for products not in pack manager - hybrid search)
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [productToAdd, setProductToAdd] = useState(null);
    const [addForm, setAddForm] = useState({
        manual_pack_size: null,
        notes: '',
    });

    // Track search mode from API response
    const [searchMode, setSearchMode] = useState('pack_manager_only');

    // Load initial data
    useEffect(() => {
        loadStats();
        loadBrands();
    }, []);

    // Load products when filters or pagination changes
    useEffect(() => {
        loadProducts();
    }, [filters, pagination.current, pagination.pageSize]);

    // Load stats
    const loadStats = async () => {
        setStatsLoading(true);
        try {
            const result = await packProductsService.getStats();
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
            message.error('Error al cargar estadísticas');
        } finally {
            setStatsLoading(false);
        }
    };

    // Load brands for filter dropdown
    const loadBrands = async () => {
        try {
            const result = await packProductsService.getBrands();
            if (result.success) {
                setBrands(result.data);
            }
        } catch (error) {
            console.error('Error loading brands:', error);
        }
    };

    // Load products
    const loadProducts = async () => {
        setLoading(true);
        try {
            // Build params, removing empty values
            const params = {
                page: pagination.current,
                pageSize: pagination.pageSize,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
            };
            
            if (filters.search) params.search = filters.search;
            if (filters.confidenceLevel) params.confidenceLevel = filters.confidenceLevel;
            if (filters.brand) params.brand = filters.brand;
            if (filters.verifiedOnly) params.verifiedOnly = true;
            if (filters.needsReview) params.needsReview = true;

            const result = await packProductsService.getList(params);
            
            if (result.success) {
                setProducts(result.data.products);
                setPagination(prev => ({
                    ...prev,
                    total: result.data.pagination.totalItems,
                }));
                // Track search mode from API response (hybrid when searching)
                setSearchMode(result.data.search_mode || 'pack_manager_only');
            }
        } catch (error) {
            console.error('Error loading products:', error);
            message.error('Error al cargar productos pack');
        } finally {
            setLoading(false);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Clear all filters
    const handleClearFilters = () => {
        setFilters({
            search: '',
            confidenceLevel: null,
            brand: null,
            verifiedOnly: false,
            needsReview: false,
            sortBy: 'sample_size',
            sortOrder: 'DESC',
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle table pagination and sorting change
    const handleTableChange = (paginationConfig, _, sorter) => {
        setPagination(prev => ({
            ...prev,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        }));
        
        if (sorter.field) {
            setFilters(prev => ({
                ...prev,
                sortBy: sorter.field,
                sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            }));
        }
    };

    // Handle edit button click
    const handleEditClick = (record) => {
        setEditingProduct(record);
        setEditForm({
            manual_pack_size: record.manual_pack_size,
            is_verified: record.is_verified,
            notes: translateNotes(record.notes) || '',
        });
        setEditModalVisible(true);
    };

    // Handle save edit
    const handleSaveEdit = async () => {
        if (!editingProduct) return;
        
        setSaving(true);
        try {
            const updateData = {
                notes: editForm.notes,
            };
            
            // Handle manual pack size
            if (editForm.manual_pack_size !== null && editForm.manual_pack_size !== '') {
                if (editForm.manual_pack_size < 2) {
                    message.error('El tamaño del pack debe ser mayor a 1');
                    setSaving(false);
                    return;
                }
                updateData.manual_pack_size = editForm.manual_pack_size;
            } else {
                // Clear manual override
                updateData.manual_pack_size = null;
            }
            
            // Handle verification - send the value if it changed
            if (editForm.is_verified !== editingProduct.is_verified) {
                updateData.is_verified = editForm.is_verified;
                if (editForm.is_verified) {
                    updateData.verified_by = user?.firstName || 'Admin';
                } else {
                    // Clear verification info when unchecking
                    updateData.verified_by = null;
                }
            }

            const result = await packProductsService.update(editingProduct.id, updateData);
            
            if (result.success) {
                message.success('Producto actualizado correctamente');
                setEditModalVisible(false);
                setEditingProduct(null);
                loadProducts();
                loadStats();
            }
        } catch (error) {
            console.error('Error updating product:', error);
            message.error(error.response?.data?.error || 'Error al actualizar producto');
        } finally {
            setSaving(false);
        }
    };

    // Handle quick verify
    const handleQuickVerify = async (record) => {
        try {
            const result = await packProductsService.update(record.id, {
                is_verified: true,
                verified_by: user?.firstName || 'Admin',
            });
            
            if (result.success) {
                message.success('Producto verificado');
                loadProducts();
                loadStats();
            }
        } catch (error) {
            console.error('Error verifying product:', error);
            message.error('Error al verificar producto');
        }
    };

    // Handle add button click (for products not in pack manager - hybrid search)
    const handleAddClick = (record) => {
        setProductToAdd(record);
        setAddForm({
            manual_pack_size: record.suggested_pack_size || 1,
            notes: '',
        });
        setAddModalVisible(true);
    };

    // Handle save add (create new pack product)
    const handleSaveAdd = async () => {
        if (!productToAdd) return;
        
        if (!addForm.manual_pack_size || addForm.manual_pack_size < 1) {
            message.error('El tamaño del pack debe ser un número mayor o igual a 1');
            return;
        }

        setSaving(true);
        try {
            const createData = {
                product_id: productToAdd.product_id,
                manual_pack_size: addForm.manual_pack_size,
                notes: addForm.notes || 'Agregado manualmente desde búsqueda híbrida',
            };

            const result = await packProductsService.create(createData);
            
            if (result.success) {
                message.success('Producto agregado a Gestión de Packs correctamente');
                setAddModalVisible(false);
                setProductToAdd(null);
                loadProducts();
                loadStats();
            }
        } catch (error) {
            console.error('Error adding product to pack manager:', error);
            const errorMsg = error.response?.data?.error || 'Error al agregar producto';
            message.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    // Handle bulk verify
    const handleBulkVerify = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Seleccione productos para verificar');
            return;
        }

        confirm({
            title: 'Verificar productos seleccionados',
            icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
            content: `¿Desea marcar ${selectedRowKeys.length} producto(s) como verificados?`,
            okText: 'Verificar',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const result = await packProductsService.bulkVerify(
                        selectedRowKeys,
                        user?.firstName || 'Admin'
                    );
                    
                    if (result.success) {
                        message.success(`${result.data.updatedCount} productos verificados`);
                        setSelectedRowKeys([]);
                        loadProducts();
                        loadStats();
                    }
                } catch (error) {
                    console.error('Error bulk verifying:', error);
                    message.error('Error al verificar productos');
                }
            },
        });
    };

    // Handle refresh inference
    const handleRefreshInference = () => {
        confirm({
            title: 'Actualizar inferencia de packs',
            icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
            content: (
                <div>
                    <p>Esto analizará las ventas recientes para detectar nuevos productos pack.</p>
                    <p style={{ color: '#8c8c8c', fontSize: '13px' }}>
                        Solo se agregarán productos nuevos. Los verificados no serán modificados.
                    </p>
                </div>
            ),
            okText: 'Actualizar',
            cancelText: 'Cancelar',
            onOk: async () => {
                setRefreshing(true);
                try {
                    const result = await packProductsService.refresh({
                        minSampleSize: 3,
                        updateExisting: false,
                    });
                    
                    if (result.success) {
                        message.success(
                            `${result.data.products_inserted} nuevos productos detectados. ` +
                            `Total: ${result.data.total_pack_products}`
                        );
                        loadProducts();
                        loadStats();
                    }
                } catch (error) {
                    console.error('Error refreshing inference:', error);
                    message.error('Error al actualizar inferencia');
                } finally {
                    setRefreshing(false);
                }
            },
        });
    };

    // Translate auto-generated English notes to Spanish
    const translateNotes = (notes) => {
        if (!notes) return '';
        
        let translated = notes;
        
        // Translate common patterns from backend auto-generated notes
        translated = translated.replace(/Auto-populated from/gi, 'Auto-poblado de');
        translated = translated.replace(/sales with splitter/gi, 'ventas con divisor');
        translated = translated.replace(/of (\d+) splitter-based sales/gi, 'de $1 ventas basadas en divisor');
        translated = translated.replace(/splitter-based sales/gi, 'ventas basadas en divisor');
        
        return translated;
    };

    // Get confidence badge class
    const getConfidenceBadge = (level, pct) => {
        // Handle null/undefined values for products not in pack manager
        if (!level || pct === null || pct === undefined) {
            return <span className="confidence-badge confidence-none">—</span>;
        }

        const classMap = {
            HIGH: 'confidence-high',
            MEDIUM: 'confidence-medium',
            LOW: 'confidence-low',
        };
        
        const labelMap = {
            HIGH: 'ALTA',
            MEDIUM: 'MEDIA',
            LOW: 'BAJA',
        };
        
        return (
            <span className={`confidence-badge ${classMap[level] || ''}`}>
                {labelMap[level] || level} ({pct.toFixed(1)}%)
            </span>
        );
    };

    // Get stock class
    const getStockClass = (stock) => {
        if (stock < 0) return 'negative';
        if (stock > 0) return 'positive';
        return 'zero';
    };

    // Get sample size indicator
    const getSampleSizeIndicator = (size) => {
        let className = 'low-sample';
        if (size >= 20) className = 'high-sample';
        else if (size >= 10) className = 'medium-sample';
        
        return (
            <span className={`sample-size-indicator ${className}`}>
                {size} ventas
            </span>
        );
    };

    // Table columns
    const columns = [
        {
            title: 'Código',
            dataIndex: 'product_code',
            key: 'product_code',
            width: 100,
            sorter: true,
            render: (code) => <strong>{code}</strong>,
        },
        {
            title: 'Descripción',
            dataIndex: 'product_description',
            key: 'product_description',
            ellipsis: true,
            render: (text, record) => (
                <div>
                    {text}
                    {record.description_mismatch === 1 && (
                        <Tooltip title="El tamaño del pack inferido no coincide con la descripción">
                            <WarningOutlined className="mismatch-indicator" />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Marca',
            dataIndex: 'brand_name',
            key: 'brand_name',
            width: 100,
            sorter: true,
            render: (brand) => <Tag color="blue">{brand}</Tag>,
        },
        {
            title: 'Tamaño Pack',
            dataIndex: 'effective_pack_size',
            key: 'effective_pack_size',
            width: 145,
            sorter: true,
            align: 'center',
            render: (_, record) => (
                <div className="pack-size-display">
                    <span className="pack-size-value">{record.effective_pack_size}</span>
                    {record.manual_pack_size && (
                        <>
                            <span className="pack-size-override">Manual</span>
                            <span className="pack-size-inferred">{record.inferred_pack_size}</span>
                        </>
                    )}
                </div>
            ),
        },
        {
            title: 'Confianza',
            dataIndex: 'confidence_level',
            key: 'confidence_level',
            width: 120,
            sorter: true,
            align: 'center',
            render: (level, record) => getConfidenceBadge(level, record.confidence_pct),
        },
        {
            title: 'Muestras',
            dataIndex: 'sample_size',
            key: 'sample_size',
            width: 120,
            sorter: true,
            align: 'center',
            render: (size) => getSampleSizeIndicator(size),
        },
        {
            title: 'Stock',
            dataIndex: 'current_stock',
            key: 'current_stock',
            width: 100,
            sorter: true,
            align: 'right',
            render: (stock) => (
                <span className={`stock-value ${getStockClass(stock)}`}>
                    {stock}
                </span>
            ),
        },
        {
            title: 'Verificado',
            dataIndex: 'is_verified',
            key: 'is_verified',
            width: 110,
            align: 'center',
            render: (verified) => verified ? (
                <span className="verified-badge">
                    <CheckCircleOutlined /> Sí
                </span>
            ) : (
                <span className="not-verified-badge">—</span>
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 120,
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <Space className="table-actions">
                    {record.in_pack_manager === 0 ? (
                        // Product NOT in pack manager - show Add button
                        <Tooltip title="Agregar a Gestión de Packs">
                            <Button 
                                type="primary"
                                size="small" 
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    if (isDemoMode) { message.info('Agregar pack — disponible en la versión completa del sistema'); return; }
                                    handleAddClick(record);
                                }}
                                className="btn-add-pack"
                            >
                                Agregar
                            </Button>
                        </Tooltip>
                    ) : (
                        // Product IS in pack manager - show Edit and Verify buttons
                        <>
                            <Tooltip title="Editar">
                                <Button 
                                    type="primary" 
                                    size="small" 
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        if (isDemoMode) { message.info('Editar tamaño de pack — disponible en la versión completa del sistema'); return; }
                                        handleEditClick(record);
                                    }}
                                />
                            </Tooltip>
                            {!record.is_verified && (
                                <Tooltip title="Verificar">
                                    <Button 
                                        type="default" 
                                        size="small" 
                                        icon={<CheckOutlined />}
                                        onClick={() => {
                                            if (isDemoMode) { message.info('Verificar pack — disponible en la versión completa del sistema'); return; }
                                            handleQuickVerify(record);
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </Space>
            ),
        },
    ];

    // Row selection config
    const rowSelection = {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            // Disable selection for verified products or products not in pack manager
            disabled: record.is_verified || record.in_pack_manager === 0,
        }),
    };

    // Get row class name
    const getRowClassName = (record) => {
        if (record.in_pack_manager === 0) return 'row-not-in-pack-manager';
        if (record.description_mismatch === 1) return 'row-mismatch';
        if (!record.is_verified && record.confidence_level !== 'HIGH') return 'row-needs-review';
        return '';
    };

    return (
        <div className="pack-products-tab">
            {/* Header Actions */}
            <div className="pack-products-header-actions">
                <Button 
                    icon={<ReloadOutlined className={refreshing ? 'refresh-spin' : ''} />}
                    onClick={() => {
                        if (isDemoMode) { message.info('Detección de nuevos packs — disponible en la versión completa del sistema'); return; }
                        handleRefreshInference();
                    }}
                    loading={!isDemoMode && refreshing}
                >
                    Detectar Nuevos
                </Button>
                <Button 
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        loadProducts();
                        loadStats();
                    }}
                    loading={loading}
                >
                    Actualizar
                </Button>
            </div>

            {/* Stats Cards */}
            {statsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            ) : stats && (
                <div className="pack-products-stats">
                    <div className="pack-stat-card">
                        <span className="stat-value">{stats.summary.total_pack_products}</span>
                        <span className="stat-label">Total Productos</span>
                    </div>
                    <div 
                        className="pack-stat-card stat-high clickable"
                        onClick={() => handleFilterChange('confidenceLevel', 'HIGH')}
                    >
                        <span className="stat-value">{stats.summary.high_confidence}</span>
                        <span className="stat-label">Alta Confianza</span>
                    </div>
                    <div 
                        className="pack-stat-card stat-medium clickable"
                        onClick={() => handleFilterChange('confidenceLevel', 'MEDIUM')}
                    >
                        <span className="stat-value">{stats.summary.medium_confidence}</span>
                        <span className="stat-label">Media Confianza</span>
                    </div>
                    <div 
                        className="pack-stat-card stat-low clickable"
                        onClick={() => handleFilterChange('confidenceLevel', 'LOW')}
                    >
                        <span className="stat-value">{stats.summary.low_confidence}</span>
                        <span className="stat-label">Baja Confianza</span>
                    </div>
                    <div 
                        className="pack-stat-card stat-verified clickable"
                        onClick={() => handleFilterChange('verifiedOnly', !filters.verifiedOnly)}
                    >
                        <span className="stat-value">{stats.summary.verified_count}</span>
                        <span className="stat-label">Verificados</span>
                    </div>
                    <div 
                        className="pack-stat-card stat-review clickable"
                        onClick={() => handleFilterChange('needsReview', !filters.needsReview)}
                    >
                        <span className="stat-value">{stats.summary.needs_review_count}</span>
                        <span className="stat-label">Requieren Revisión</span>
                    </div>
                    {stats.summary.negative_stock_count > 0 && (
                        <div className="pack-stat-card stat-negative">
                            <span className="stat-value">{stats.summary.negative_stock_count}</span>
                            <span className="stat-label">Stock Negativo</span>
                        </div>
                    )}
                </div>
            )}

            {/* Filters */}
            <div className="pack-products-filters">
                <div className="pack-products-filters-row">
                    <div className="filter-group">
                        <span className="filter-label">Buscar</span>
                        <Input
                            className="filter-input"
                            placeholder="Código o descripción..."
                            prefix={<SearchOutlined />}
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            allowClear
                            disabled={isDemoMode}
                        />
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">Confianza</span>
                        <Select
                            className="filter-select"
                            placeholder="Todas"
                            value={filters.confidenceLevel}
                            onChange={(value) => handleFilterChange('confidenceLevel', value)}
                            allowClear
                            disabled={isDemoMode}
                        >
                            <Option value="HIGH">Alta (HIGH)</Option>
                            <Option value="MEDIUM">Media (MEDIUM)</Option>
                            <Option value="LOW">Baja (LOW)</Option>
                        </Select>
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">Marca</span>
                        <Select
                            className="filter-select"
                            placeholder="Todas las marcas"
                            value={filters.brand}
                            onChange={(value) => handleFilterChange('brand', value)}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                String(option.value ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={isDemoMode}
                        >
                            {brands.map(b => (
                                <Option key={b.brand_name} value={b.brand_name}>
                                    {b.brand_name} ({b.product_count})
                                </Option>
                            ))}
                        </Select>
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">&nbsp;</span>
                        <Checkbox
                            checked={filters.verifiedOnly}
                            onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                            disabled={isDemoMode}
                        >
                            Solo verificados
                        </Checkbox>
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">&nbsp;</span>
                        <Checkbox
                            checked={filters.needsReview}
                            onChange={(e) => handleFilterChange('needsReview', e.target.checked)}
                            disabled={isDemoMode}
                        >
                            Requieren revisión
                        </Checkbox>
                    </div>
                    
                    <div className="filter-buttons">
                        <Button 
                            icon={<ClearOutlined />} 
                            onClick={handleClearFilters}
                            disabled={isDemoMode}
                        >
                            Limpiar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Hybrid Mode Notice */}
            {searchMode === 'hybrid' && filters.search && (
                <div className="hybrid-mode-notice">
                    <SearchOutlined style={{ marginRight: 8 }} />
                    <span>
                        <strong>Búsqueda híbrida activa:</strong> Se muestran todos los productos que coinciden con "{filters.search}".
                        Los productos sin fondo amarillo ya están en Gestión de Packs.
                    </span>
                </div>
            )}

            {/* Bulk Selection Bar */}
            {selectedRowKeys.length > 0 && (
                <div className="bulk-selection-bar">
                    <div className="bulk-selection-info">
                        <span className="bulk-selection-count">
                            {selectedRowKeys.length} producto(s) seleccionado(s)
                        </span>
                    </div>
                    <div className="bulk-selection-actions">
                        <Button onClick={() => setSelectedRowKeys([])}>
                            Cancelar selección
                        </Button>
                        <Button 
                            type="primary" 
                            icon={<CheckCircleOutlined />}
                            onClick={handleBulkVerify}
                        >
                            Verificar seleccionados
                        </Button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="pack-products-table-container">
                <Table
                    rowKey={(record) => record.id || `new-${record.product_id}`}
                    columns={columns}
                    dataSource={products}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} de ${total} productos`,
                    }}
                    onChange={handleTableChange}
                    rowSelection={rowSelection}
                    rowClassName={getRowClassName}
                    scroll={{ x: 1200 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No se encontraron productos pack"
                            />
                        ),
                    }}
                />
            </div>

            {/* Edit Modal */}
            <Modal
                title={
                    <span>
                        <EditOutlined style={{ marginRight: 8 }} />
                        Editar Definición de Stock de Producto
                    </span>
                }
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingProduct(null);
                }}
                onOk={handleSaveEdit}
                confirmLoading={saving}
                okText="Guardar"
                cancelText="Cancelar"
                className="pack-edit-modal"
                width={600}
            >
                {editingProduct && (
                    <div>
                        {/* Product Info */}
                        <div className="product-info-section">
                            <div className="product-info-title">
                                {editingProduct.product_description}
                            </div>
                            <div className="product-info-code">
                                Código: {editingProduct.product_code}
                            </div>
                            <div className="product-info-brand">
                                {editingProduct.brand_name}
                            </div>
                        </div>

                        {/* Inferred Pack Size Info */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Tamaño Inferido:</span>
                            <div className="edit-form-value">
                                <strong>{editingProduct.inferred_pack_size}</strong>
                                {' '}
                                {getConfidenceBadge(editingProduct.confidence_level, editingProduct.confidence_pct)}
                                {' '}
                                <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
                                    ({editingProduct.sample_size} ventas analizadas)
                                </span>
                            </div>
                        </div>

                        {/* Manual Override */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">
                                Tamaño Manual:
                                <Tooltip title="Sobrescribe el tamaño inferido. Deje vacío para usar el valor inferido.">
                                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                </Tooltip>
                            </span>
                            <div className="edit-form-value">
                                <InputNumber
                                    min={2}
                                    max={9999}
                                    value={editForm.manual_pack_size}
                                    onChange={(value) => setEditForm(prev => ({
                                        ...prev,
                                        manual_pack_size: value,
                                    }))}
                                    placeholder="Vacío = usar inferido"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* Verification */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Verificación:</span>
                            <div className="edit-form-value">
                                <Checkbox
                                    checked={editForm.is_verified}

                                    onChange={(e) => setEditForm(prev => ({
                                        ...prev,
                                        is_verified: e.target.checked,
                                    }))}
                                >
                                    {editingProduct.is_verified 
                                        ? `Verificado por ${editingProduct.verified_by} el ${new Date(editingProduct.verified_at).toLocaleDateString()}`
                                        : 'Marcar como verificado'
                                    }
                                </Checkbox>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Notas:</span>
                            <div className="edit-form-value">
                                <Input.TextArea
                                    rows={3}
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm(prev => ({
                                        ...prev,
                                        notes: e.target.value,
                                    }))}
                                    placeholder="Notas adicionales sobre este producto..."
                                    maxLength={500}
                                    showCount
                                />
                            </div>
                        </div>

                        {/* Current Stock Info */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Stock Actual:</span>
                            <div className="edit-form-value">
                                <span className={`stock-value ${getStockClass(editingProduct.current_stock)}`}>
                                    {editingProduct.current_stock} unidades
                                </span>
                                {' = '}
                                <strong>
                                    {Math.floor(editingProduct.current_stock / (editForm.manual_pack_size || editingProduct.effective_pack_size))}
                                </strong>
                                {' packs de '}
                                {editForm.manual_pack_size || editingProduct.effective_pack_size}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Add Modal (for products discovered via hybrid search - not in pack manager) */}
            <Modal
                title={
                    <span>
                        <PlusOutlined style={{ marginRight: 8 }} />
                        Agregar a Gestión de Packs
                    </span>
                }
                open={addModalVisible}
                onCancel={() => {
                    setAddModalVisible(false);
                    setProductToAdd(null);
                }}
                onOk={handleSaveAdd}
                confirmLoading={saving}
                okText="Agregar"
                cancelText="Cancelar"
                className="pack-add-modal"
                width={600}
            >
                {productToAdd && (
                    <div>
                        {/* Product Info */}
                        <div className="product-info-section">
                            <div className="product-info-title">
                                {productToAdd.product_description}
                            </div>
                            <div className="product-info-code">
                                Código: {productToAdd.product_code}
                            </div>
                            <div className="product-info-brand">
                                {productToAdd.brand_name}
                            </div>
                        </div>

                        {/* Suggestion Notice */}
                        {productToAdd.suggested_pack_size && productToAdd.suggested_pack_size > 1 && (
                            <div className="suggestion-notice">
                                <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8, fontSize: 18 }} />
                                <span>
                                    Se detectó <strong>"{productToAdd.suggested_pack_size}"</strong> en la descripción del producto.
                                    Verifique que este valor sea correcto.
                                </span>
                            </div>
                        )}

                        {/* Pack Size Input */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">
                                Tamaño del Pack:
                                <Tooltip title="Cantidad de unidades individuales por pack/sobre/caja">
                                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />
                                </Tooltip>
                            </span>
                            <div className="edit-form-value">
                                <InputNumber
                                    min={1}
                                    max={9999}
                                    value={addForm.manual_pack_size}
                                    onChange={(value) => setAddForm(prev => ({
                                        ...prev,
                                        manual_pack_size: value,
                                    }))}
                                    placeholder="Ej: 100, 200, 500"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Notas:</span>
                            <div className="edit-form-value">
                                <Input.TextArea
                                    rows={3}
                                    value={addForm.notes}
                                    onChange={(e) => setAddForm(prev => ({
                                        ...prev,
                                        notes: e.target.value,
                                    }))}
                                    placeholder="Ej: Confirmado con proveedor, sobre de 200 unidades"
                                    maxLength={500}
                                    showCount
                                />
                            </div>
                        </div>

                        {/* Stock Preview */}
                        <div className="edit-form-row">
                            <span className="edit-form-label">Stock Actual:</span>
                            <div className="edit-form-value">
                                <span className={`stock-value ${getStockClass(productToAdd.current_stock)}`}>
                                    {productToAdd.current_stock} packs
                                </span>
                                {addForm.manual_pack_size && addForm.manual_pack_size > 1 && (
                                    <>
                                        {' = '}
                                        <strong>
                                            {(productToAdd.current_stock * addForm.manual_pack_size).toLocaleString('es-AR')}
                                        </strong>
                                        {' unidades individuales'}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PackProductsTab;
